/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import * as url from '../../helpers/url_helper';
import withRouter from 'Components/Common/withRouter';
import toastMessages from 'Components/Common/messages/toastMessages';
import pdfIcon from '../../assets/images/commons/pdf.svg';
import captian from '../../../src/assets/images/commons/captain.jpeg';
import profilePlaceholder from '../../../src/assets/images/commons/dummy-image.jpg';
import dummyUser from '../../../src/assets/images/commons/dummyUser.jpeg';

import cameraIcon from '../../../src/assets/images/commons/camera.svg';
import { relative } from 'path';
import { AuthUser, displayFirstLastName } from 'Components/Common/Util';

interface DropzoneComponentProps {
    noOfFilesAccepted?: number;
    fieldName: string;
    type: string;
    files: any;
    setDocuments?: (event: any) => void;
    handleOnChange: (event: any) => void;
    handleDeleteFile?: any;
    onBlur: (fieldName: string, files: File[]) => void;
    values?: {
        uploadType?: {
            label: string;
        };
        type?: {
            label: string;
        };
    };
    field?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        isUploadButton?: boolean;
        isNoteAvailable?: string;
        label: string;
    };
    showHeader?: boolean;
    label: string;
    accept: Accept;
    maxSize: number;
    isMultipleFiles: boolean;
    fileTypeMsg: string;
    setProfilePic?: any;
    required?: boolean;
    disabled?: boolean;
    mode?: string;
    router: { apiClient: any };
    dropzoneheight: any;
    profile?: boolean;
    profileEditButton?: boolean;
}

const DropzoneComponent: React.FC<DropzoneComponentProps> = (props) => {
    const api = props.router.apiClient;
    const userDetails = AuthUser();
    const [type, setType] = useState<string>('');
    const [file1, setFile] = useState<File[]>([]);
    const [getDocuments, setGetDocuments] = useState<File[]>([]);
    const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [maxFilesAccepted, setMaxFilesAccepted] = useState<number>(props?.noOfFilesAccepted || 1);
    const profileUrl = process.env.REACT_APP_API_URL + url.VIEW_DOCUMENTS + '/';
    const iconRefs = useRef<{ [key: number]: HTMLSpanElement | null }>({});
    const inputRef = useRef<any>(null);

    // Documents upload API
    const handleUploadDocuments = (files: any) => {
        setButtonDisabled(true);
        const formData = new FormData();
        const uploadDocuments: any = [];
        files.forEach((ele: any) => {
            if (!ele.imgUrl) formData.append('files', ele);
            else uploadDocuments.push(ele);
        });
        api.create(url.UPLOAD_DOCUMENTS, formData).then((res: any) => {
            if (res.status === 'SUCCESS') {
                const documents = [...uploadDocuments, ...res.data];
                toast.success(toastMessages.documentsUploaded);
                props?.setDocuments &&
                    props.setDocuments({
                        target: {
                            name: props.fieldName,
                            type: props.type,
                            files: documents
                        }
                    });
            } else {
                toast.error('Something went wrong...');
            }
        });
    };

    // Function to convert bytes to megabytes
    function bytesToMegabytes(bytes: any) {
        return (bytes / (1024 * 1024)).toFixed(0);
    }

    const handleAcceptedFiles = (files: File[], fieldName: string, fileRejections: FileRejection[]) => {
        setGetDocuments([...files]);
        const checkLength = file1.length + files.length;
        const selectedDocuments = props?.files;
        if (fileRejections?.length > 0) {
            fileRejections.forEach((file) => {
                if (file?.errors[0]?.code === 'file-too-large') {
                    const fileSizeInMB = bytesToMegabytes(props.maxSize); // Convert bytes to megabytes
                    toast.error(`File is larger than ${fileSizeInMB} MB`);
                } else {
                    toast.error(file.errors[0].message); // If error code is not recognized, return original message
                }
                return;
            });
        }

        if (maxFilesAccepted >= checkLength) {
            files.map((file) => {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size)
                });
                return selectedDocuments.push(file);
            });
            setFile([...selectedDocuments]);
            setButtonDisabled(false);
        } else {
            toast.error(`Please don't select more than ${maxFilesAccepted} files`);
        }

        props.handleOnChange({
            target: {
                name: fieldName,
                type: props.type,
                files: selectedDocuments,
                action: 'UPLOAD'
            }
        });
        // handleUploadDocuments(selectedDocuments)
        if (files?.length > 0) {
            props.onBlur(fieldName, files);
        }
    };

    /**
     * Formats the size
     */
    const formatBytes = (bytes: number, decimals = 2): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const onRemoveImage = (fieldName: string, file: any, index: number) => {
        handleRemoveFile(fieldName, file, index);
    };

    const handleRemoveFile = (fieldName: string, file: any, index: number) => {
        if (props?.handleDeleteFile) props?.handleDeleteFile(fieldName, file, index);

        // eslint-disable-next-line no-shadow
        const list = props?.files.filter((file: any, i: number) => i !== index);
        setFile([...list]);
        props.handleOnChange({
            target: {
                name: fieldName,
                type: props.type,
                files: list,
                file,
                action: 'DELETE'
            }
        });
        props.onBlur(fieldName, []);
        if (fieldName === 'documents') {
            props?.setDocuments &&
                props?.setDocuments({
                    target: {
                        name: props.fieldName,
                        type: props.type,
                        files: list
                    }
                });
        }
    };

    useEffect(() => {
        if (props?.values) {
            if (props?.values?.uploadType?.label) {
                setType(props?.values?.uploadType?.label);
            } else {
                setType(props?.values?.type?.label ? props?.values?.type?.label : '');
            }
        }
    }, [props.values]);
    const handleCameraClick = () => {
        if (props.files.length > 0) {
            // Remove existing image first
            onRemoveImage(props.fieldName, props.files[0], 0);
        }

        // Delay file picker slightly to ensure state updates first
        setTimeout(() => {
            inputRef.current?.click();
        }, 10);
    };

    return (
        <>
            {!props?.profile && (
                <div className="widget-element">
                    {' '}
                    <Row>
                        <Col xs={props.field?.xs} sm={props.field?.sm} md={props.field?.md} lg={props.field?.lg} xl={props.field?.xl}>
                            <Card
                                className={`border br-10 dropzone ${props.dropzoneheight ? `min-height-${props.dropzoneheight}` : ''}  ${
                                    props.disabled ? 'dropzone-disabled' : ''
                                }`}
                            >
                                {props.showHeader && (
                                    <CardHeader>
                                        <h4 className="card-title mb-0">
                                            {props.label ? props.label : props?.field?.label ? props?.field?.label : ''}
                                        </h4>
                                    </CardHeader>
                                )}
                                <CardBody className="d-flex justify-content-center align-items-center">
                                    {(maxFilesAccepted > props?.files.length ? true : props?.files.length === 0 ? true : false) && (
                                        <>
                                            <Dropzone
                                                onDrop={(acceptedFiles, fileRejections) => {
                                                    handleAcceptedFiles(acceptedFiles, props.fieldName, fileRejections);
                                                }}
                                                maxFiles={maxFilesAccepted}
                                                accept={props.accept}
                                                minSize={0}
                                                maxSize={props.maxSize}
                                            >
                                                {({ getRootProps, getInputProps }) => (
                                                    <div
                                                        className={`${props.mode === 'sign' ? 'p-2' : ' pb-1'} dz-clickable cursor-pointer`}
                                                    >
                                                        <div
                                                            className="d-flex justify-content-evenly align-items-center dz-message needsclick"
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            {/* <div>
                                                        <i className="fs-22 text-muted ri-upload-cloud-2-fill" />
                                                    </div>
                                                    <p className="fs-14 text-muted fw-semibold mb-0">Upload.</p> */}
                                                            <div className="d-flex align-items-center gap-1">
                                                                <img alt={'Upload-Img'} src={pdfIcon}></img>
                                                                {/* <i className="fs-22 text-muted ri-upload-cloud-2-fill" />
                                                        <p className="fs-14 text-muted fw-semibold mb-0">Upload</p> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        </>
                                    )}
                                    {props?.isMultipleFiles && props?.files?.length > 0 && <div className="pt-4"></div>}
                                    <div className="list-unstyled mb-0" id="file-previews">
                                        {props?.files?.length > 0 &&
                                            props?.files?.map(
                                                (
                                                    f: {
                                                        name:
                                                            | string
                                                            | number
                                                            | boolean
                                                            | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                                                            | Iterable<React.ReactNode>
                                                            | null
                                                            | undefined;
                                                        preview: string | undefined;
                                                        formattedSize:
                                                            | string
                                                            | number
                                                            | boolean
                                                            | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                                                            | Iterable<React.ReactNode>
                                                            | React.ReactPortal
                                                            | Iterable<React.ReactNode>
                                                            | null
                                                            | undefined;
                                                    },
                                                    i: number
                                                ) => {
                                                    return (
                                                        <Card
                                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-primary dz-complete"
                                                            key={i + '-file'}
                                                        >
                                                            <div
                                                                style={{ color: '#000000' }}
                                                                className="d-flex align-items-center justify-content-between"
                                                            >
                                                                <div className="p-2 w-75">
                                                                    <Row className="align-items-center">
                                                                        <Col className="col-auto">
                                                                            {/* {props.fieldName !== 'documents' && (
                                                                                <img
                                                                                    data-dz-thumbnail=""
                                                                                    height="80"
                                                                                    className="avatar-sm rounded bg-light"
                                                                                    alt={'avatar'}
                                                                                    src={f.preview ? f.preview : undefined}
                                                                                />
                                                                            )} */}
                                                                        </Col>
                                                                        <Col>
                                                                            <Link to="#" className="text-muted font-weight-bold">
                                                                                <span className="fs-14">{f.name}</span>
                                                                            </Link>
                                                                            <p className="mb-0">
                                                                                <strong>{f.formattedSize}</strong>
                                                                            </p>
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                                <div className="p-2">
                                                                    {(f?.formattedSize ||
                                                                        f?.preview ||
                                                                        props.fieldName === 'logo' ||
                                                                        props.fieldName === 'profileImg' ||
                                                                        props.files.length > 0) && (
                                                                        <i
                                                                            className="ri-close-circle-line cursor-pointer  link-radiogramPrimary font-size-22"
                                                                            onClick={() => onRemoveImage(props.fieldName, f, i)}
                                                                            title="remove"
                                                                        ></i>
                                                                        //                                                             position: absolute;
                                                                        // top: 11px;
                                                                        // right: 10px;
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Card>
                                                    );
                                                }
                                            )}
                                    </div>
                                    <p className="text-danger text-center pt-1 m-0 fs-10 fw-medium">{props?.fileTypeMsg}</p>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* {props?.field?.isUploadButton && (
                    <Col lg={4}>
                        <div className="my-1 d-flex flex-column justify-content-center align-items-center">
                            <Label>&emsp;</Label>
                            <button
                                type="button"
                                className="btn btn-radiogramGreen waves-effect waves-light"
                                onClick={() => handleUploadDocuments()}
                                disabled={isButtonDisabled}
                            >
                                <span className="d-flex align-items-center gap-2">
                                    <i className="ri-upload-2-line"></i>
                                    Upload
                                </span>
                            </button>
                        </div>
                    </Col>
                )} */}
                        {props?.field?.isNoteAvailable && (
                            <Row>
                                <Col>
                                    <span className="badge-soft-warning fs-14 p-2">Note : {props?.field?.isNoteAvailable}</span>
                                </Col>
                            </Row>
                        )}
                    </Row>
                </div>
            )}

            {props?.profile && (
                <>
                    {(maxFilesAccepted > props?.files.length ? true : props?.files.length === 0 ? true : false) && (
                        <>
                            <Dropzone
                                onDrop={(acceptedFiles, fileRejections) => {
                                    handleAcceptedFiles(acceptedFiles, props.fieldName, fileRejections);
                                }}
                                maxFiles={maxFilesAccepted}
                                accept={props.accept}
                                minSize={0}
                                maxSize={props.maxSize}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <div style={{ position: 'relative' }}>
                                        <div>
                                            {/* src={profilePlaceholder} */}
                                            {props.files && props.files.length > 0 && props.files?.[0]?.preview ? (
                                                <img
                                                    className="width-100 height-100 profile-image"
                                                    alt="Upload-Img"
                                                    src={profileUrl + props.files[0]?.preview}
                                                />
                                            ) : (
                                                // <img
                                                //     className="width-100 height-100 profile-image display-block"
                                                //     alt="Uploadddddd-Img"
                                                //     src={dummyUser}
                                                // />
                                                <span className="text-dark bg-grey profile-background-color rounded-circle d-inline-flex justify-content-center align-items-center width-100-px height-100-px font-size-33 font-weight-600 border border-3 border-primary">
                                                    {displayFirstLastName()}
                                                </span>
                                            )}

                                            {/* <i className="fs-22 text-muted ri-upload-cloud-2-fill" />
                                                        <p className="fs-14 text-muted fw-semibold mb-0">Upload</p> */}
                                        </div>
                                        {props?.profileEditButton && (
                                            <div className="profile-chooser-icon" onClick={handleCameraClick}>
                                                <input {...getInputProps()} ref={inputRef} style={{ display: 'none' }} />
                                                <img src={cameraIcon} alt="Please Input your profile" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Dropzone>
                            <div className="list-unstyled mb-0" id="file-previews">
                                {props?.files?.length > 0 &&
                                    props?.files?.map(
                                        (
                                            f: {
                                                name:
                                                    | string
                                                    | number
                                                    | boolean
                                                    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                                                    | Iterable<React.ReactNode>
                                                    | null
                                                    | undefined;
                                                preview: string | undefined;
                                                formattedSize:
                                                    | string
                                                    | number
                                                    | boolean
                                                    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                                                    | Iterable<React.ReactNode>
                                                    | React.ReactPortal
                                                    | Iterable<React.ReactNode>
                                                    | null
                                                    | undefined;
                                            },
                                            i: number
                                        ) => {
                                            return (
                                                <div
                                                    className="p-2"
                                                    onClick={() => onRemoveImage(props.fieldName, f, i)}
                                                    ref={(el) => (iconRefs.current[i] = el)}
                                                ></div>
                                            );
                                        }
                                    )}
                            </div>
                            <p className="text-danger text-center pt-1 m-0 fs-10 fw-medium">{props?.fileTypeMsg}</p>
                        </>
                    )}
                </>
            )}

            {/* )} */}
        </>
    );
};

export default withRouter(DropzoneComponent);
